/* eslint-disable import/no-webpack-loader-syntax */
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { useTranslation } from "react-i18next";
import LocationPopup from "./LocationPopup";
import Button from "../../../Common/FormElements/Button/Button";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// singleMarker: only one point allowed — adding a new one replaces the
// previous (used by the app-customer modal; prospects keep multi-marker).
function Location({
  initialValues = {},
  handleFormChange = () => {},
  singleMarker = false,
}) {
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const defaultValue = [1.7191036, 46.71109];
  const { t, i18n } = useTranslation("common");

  const [currentMarker, setCurrentMarker] = useState(null);
  const [formData, setFormData] = useState(initialValues ? initialValues : {});

  const [markers, setMarkers] = useState([]);
  const [showGeocoder, setShowGeocoder] = useState(false);

  const hasSameMarker = (coordinates) =>
    markers.some(
      (m) =>
        Array.isArray(m.coordinates) &&
        Array.isArray(coordinates) &&
        m.coordinates[0] === coordinates[0] &&
        m.coordinates[1] === coordinates[1],
    );

  const isSameMarker = (coordinates, comparedCoordinates) => {
    return (
      Array.isArray(coordinates) &&
      Array.isArray(comparedCoordinates) &&
      coordinates[0] === comparedCoordinates[0] &&
      coordinates[1] === comparedCoordinates[1]
    );
  };

  const addMarker = (coordinates) => {
    if (!Array.isArray(coordinates)) return;
    if (hasSameMarker(coordinates)) {
      return;
    }
    const marker = new mapboxgl.Marker({ draggable: true })
      .setLngLat(coordinates)
      .addTo(mapRef.current);

    let isDragging = false;

    marker.on("dragstart", () => {
      isDragging = true;
      setCurrentMarker(null);
    });

    marker.on("dragend", () => {
      isDragging = false;
    });

    marker.getElement().addEventListener("click", (e) => {
      if (!isDragging) {
        if (isSameMarker(marker.getLngLat().toArray(), currentMarker)) {
          setCurrentMarker(null);
        } else {
          setCurrentMarker(marker.getLngLat().toArray());
        }
      }
    });

    setMarkers((prev) => {
      if (singleMarker) {
        prev.forEach((m) => m.marker.remove());
        return [{ marker, coordinates }];
      }
      return [...prev, { marker, coordinates }];
    });

    marker.on("dragend", () => {
      const { lng, lat } = marker.getLngLat();
      setMarkers((prev) =>
        prev.map((m) =>
          m.marker === marker ? { ...m, coordinates: [lng, lat] } : m,
        ),
      );
    });
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      markers: markers.map((m) => ({ coordinates: m.coordinates })),
    }));
  }, [markers]);

  const mapboxInstance = () =>
    new mapboxgl.Map({
      container: mapContainerRef.current,
      center:
        formData?.markers && formData.markers.length > 0
          ? formData.markers[0].coordinates
          : defaultValue,
      style: "mapbox://styles/mapbox/light-v10",
      zoom: 2,
      interactive: true,
      language: i18n.language?.includes("en") ? "en" : "fr",
    });

  useEffect(() => {
    mapboxgl.container = "map";
    mapRef.current = mapboxInstance();

    mapRef.current.on("load", () => {
      if (formData.markers && formData.markers.length > 0) {
        formData.markers.forEach((m) => addMarker(m.coordinates));
      }
    });

    return () => {
      markers.forEach((m) => m.marker.remove());
      mapRef.current.remove();
    };
  }, []);

  useEffect(() => {
    let geocoder;
    if (showGeocoder && mapRef.current) {
      geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        // countries:
        //   "al,ad,am,at,az,by,be,ba,bg,hr,cy,cz,dk,ee,fi,fr,ge,de,gr,hu,is,ie,it,kz,xk,lv,li,lt,lu,mt,md,mc,me,nl,mk,no,pl,pt,ro,ru,sm,rs,sk,si,es,se,ch,tr,ua,gb,va,ma,dz,tn,ly,mr",
        language: i18n.language?.includes("en") ? "en" : "fr",
      });
      geocoder.on("result", ({ result }) => {
        const newLngLat = result.center;
        addMarker(newLngLat);
        setShowGeocoder(false);
      });
      const container = document.getElementById("geocoder-container");
      if (container) {
        container.appendChild(geocoder.onAdd(mapRef.current));
      }
    }
    return () => {
      if (document.getElementById("geocoder-container")) {
        document.getElementById("geocoder-container").innerHTML = "";
      }
    };
  }, [showGeocoder, mapRef]);

  useEffect(() => {
    handleFormChange("geoEntity", formData);
  }, [formData]);

  return (
    <div className="p-4 d-flex flex-column gap-4 h-100 position-relative location">
      <div className="position-absolute top-0 left-0 z-1 pt-5 ps-3">
        <Button
          onClick={() => setShowGeocoder((prev) => !prev)}
          style={{ width: "fit-content", marginBottom: 8 }}
        >
          {t("Add an address")}
        </Button>

        <div
          id="geocoder-container"
          style={{ zIndex: 2, width: 350, marginBottom: 8 }}
        />
      </div>
      <div
        id="map-container"
        className="map h-100 w-100"
        ref={mapContainerRef}
      />
      <div id="calculated-area" />
      {currentMarker && (
        <LocationPopup
          map={mapRef.current}
          activeFeature={currentMarker}
          onClose={() => setCurrentMarker(null)}
          handleDelete={() => {
            setMarkers((prev) => {
              prev.forEach((m) => {
                if (
                  Array.isArray(m.coordinates) &&
                  m.coordinates[0] === currentMarker[0] &&
                  m.coordinates[1] === currentMarker[1]
                ) {
                  m.marker.remove();
                }
              });
              return prev.filter(
                (m) =>
                  !(
                    Array.isArray(m.coordinates) &&
                    m.coordinates[0] === currentMarker[0] &&
                    m.coordinates[1] === currentMarker[1]
                  ),
              );
            });
            setFormData((prev) => ({
              ...prev,
              markers: (prev.markers || []).filter(
                (m) =>
                  !(
                    Array.isArray(m.coordinates) &&
                    Array.isArray(currentMarker) &&
                    m.coordinates[0] === currentMarker[0] &&
                    m.coordinates[1] === currentMarker[1]
                  ),
              ),
            }));
            setCurrentMarker(null);
          }}
        />
      )}
    </div>
  );
}

export default Location;
